import { extractModelRootsFromManifest } from '../../utils/companyArtifactModelRoots';
import type { KryspaceRenderManifest } from '../../types/kryspaceRenderManifest';
import type { KryspaceControlBridgeStructure } from '../../types/kryspaceControlBridgeStructure';
import type { McEngineeringWorkspacesModel } from './missionControl/mcEngineeringWorkspaces';

export type ControlBridgeModelOptionSource =
  | 'current'
  | 'primary'
  | 'personal'
  | 'linked-workspace'
  | 'engineering-workspace'
  | 'structure-node';

export interface ControlBridgeModelOption {
  slug: string;
  title: string;
  statusPhrase?: string;
  /** CB-DIB-002 guidance: what the sub-model is for. */
  purpose?: string;
  /** CB-DIB-002: open_workspace | read_only_rollup | delivery_queue. */
  recommendedAction?: string;
  source: ControlBridgeModelOptionSource;
}

function collectStructureLinkedSlugs(
  structure: KryspaceControlBridgeStructure
): Array<{
  slug: string;
  label: string;
  purpose?: string;
  recommendedAction?: string;
}> {
  const out: Array<{
    slug: string;
    label: string;
    purpose?: string;
    recommendedAction?: string;
  }> = [];
  const walk = (nodes: KryspaceControlBridgeStructure['groups'][number]['nodes']) => {
    for (const node of nodes) {
      if (node.linkedWorkspaceSlug) {
        out.push({
          slug: node.linkedWorkspaceSlug,
          label: node.label,
          purpose: node.purpose?.trim() || undefined,
          recommendedAction: node.recommendedAction?.trim() || undefined,
        });
      }
      if (node.children?.length) walk(node.children);
    }
  };
  for (const group of structure.groups) {
    walk(group.nodes);
  }
  return out;
}

/** Deduped company-model / Control Bridge workspace options for header + sidebar selectors. */
export function buildControlBridgeModelOptions(input: {
  workspaceSlug: string;
  companyTitle: string;
  manifest: KryspaceRenderManifest;
  structure: KryspaceControlBridgeStructure;
  mcEngineeringWorkspaces?: McEngineeringWorkspacesModel | null;
  /** Company home / parent CB slug when viewing a linked or engineering submodel. */
  primaryWorkspaceSlug?: string | null;
  primaryWorkspaceTitle?: string | null;
  personalWorkspaceSlug?: string | null;
  personalWorkspaceTitle?: string | null;
}): ControlBridgeModelOption[] {
  const currentSlug = input.workspaceSlug.trim();
  const primarySlug = input.primaryWorkspaceSlug?.trim() || null;
  const seen = new Set<string>();
  const options: ControlBridgeModelOption[] = [];

  const push = (option: ControlBridgeModelOption) => {
    const slug = option.slug.trim();
    if (!slug) return;
    if (seen.has(slug)) {
      // Enrich earlier entry with CB-DIB-002 guidance when structure arrives later.
      const existing = options.find((o) => o.slug === slug);
      if (!existing) return;
      if (option.purpose && !existing.purpose) existing.purpose = option.purpose;
      if (option.recommendedAction && !existing.recommendedAction) {
        existing.recommendedAction = option.recommendedAction;
      }
      // Prefer purpose/action statusPhrase over role-only phrases (e.g. component_model).
      if (option.purpose || option.recommendedAction) {
        if (option.statusPhrase) existing.statusPhrase = option.statusPhrase;
      } else if (option.statusPhrase && !existing.statusPhrase) {
        existing.statusPhrase = option.statusPhrase;
      }
      return;
    }
    seen.add(slug);
    options.push({ ...option, slug });
  };

  if (primarySlug && primarySlug !== currentSlug) {
    push({
      slug: primarySlug,
      title: input.primaryWorkspaceTitle?.trim() || primarySlug,
      statusPhrase: 'Primary model',
      source: 'primary',
    });
  }

  const personalSlug = input.personalWorkspaceSlug?.trim() || null;
  if (personalSlug && personalSlug !== currentSlug && personalSlug !== primarySlug) {
    push({
      slug: personalSlug,
      title: input.personalWorkspaceTitle?.trim() || 'Personal model',
      statusPhrase: 'Personal',
      source: 'personal',
    });
  }

  push({
    slug: currentSlug,
    title: input.companyTitle.trim() || currentSlug,
    source: 'current',
  });

  for (const root of extractModelRootsFromManifest(input.manifest, currentSlug)) {
    if (root.slug === currentSlug) continue;
    push({
      slug: root.slug,
      title: root.title,
      statusPhrase: root.statusPhrase,
      source: 'linked-workspace',
    });
  }

  for (const link of input.mcEngineeringWorkspaces?.links ?? []) {
    if (!link.reachable) continue;
    push({
      slug: link.engineeringWorkspaceSlug,
      title: link.displayName,
      statusPhrase: link.roleSummary || undefined,
      source: 'engineering-workspace',
    });
  }

  for (const node of collectStructureLinkedSlugs(input.structure)) {
    const guidance = [node.purpose, node.recommendedAction].filter(Boolean).join(' · ');
    push({
      slug: node.slug,
      title: node.label,
      statusPhrase: guidance || undefined,
      purpose: node.purpose,
      recommendedAction: node.recommendedAction,
      source: 'structure-node',
    });
  }

  return options.sort((a, b) => {
    // Primary first (when present), then personal, then current, then alpha
    if (a.source === 'primary' && b.source !== 'primary') return -1;
    if (b.source === 'primary' && a.source !== 'primary') return 1;
    if (a.source === 'personal' && b.source !== 'personal') return -1;
    if (b.source === 'personal' && a.source !== 'personal') return 1;
    if (a.slug === currentSlug) return -1;
    if (b.slug === currentSlug) return 1;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });
}

function looksLikeModelMachinery(text: string): boolean {
  const t = text.trim();
  if (!t) return true;
  if (/^(ca-|ai-|co-|pt-|pf-)/i.test(t)) return true;
  if (/^[a-z0-9]+(_[a-z0-9-]+)+$/i.test(t)) return true;
  if (/^(CREATED|INITIALIZED|DRAFTED|ACTIVE|CLOSED|VALIDATED)$/i.test(t)) return true;
  return false;
}

/** Picker label — display name only, never a slug or item id. */
export function modelOptionClientLabel(option: ControlBridgeModelOption): string {
  const title = option.title.trim();
  if (title && !looksLikeModelMachinery(title)) return title;
  if (option.source === 'personal') return 'Personal model';
  if (option.source === 'primary') return 'Primary model';
  return 'Workspace';
}

/** CB-DOM-008: picker and Drive phrases use these words, not the stored enum. */
const GUIDANCE_ACTION_LABELS: Record<string, string> = {
  open_workspace: 'Navigate',
  read_only_rollup: 'Rollup',
  delivery_queue: 'Delivery queue',
};

export function guidanceActionLabel(action: string | undefined | null): string | null {
  const key = action?.trim();
  if (!key) return null;
  return GUIDANCE_ACTION_LABELS[key] ?? null;
}

/** Replace stored recommended_action tokens inside a status phrase. */
export function labelGuidancePhrase(phrase: string | undefined | null): string | null {
  const raw = phrase?.trim();
  if (!raw) return null;
  const labeled = raw
    .split(' · ')
    .map((part) => GUIDANCE_ACTION_LABELS[part] ?? part)
    .join(' · ')
    .trim();
  return labeled || null;
}

export function modelOptionClientMeta(option: ControlBridgeModelOption): string | null {
  const phrase = labelGuidancePhrase(option.statusPhrase);
  if (!phrase || looksLikeModelMachinery(phrase)) return null;
  if (phrase === 'runtime') return null;
  return phrase;
}
